/**
 * LandingBookCarousel — reusable carousel for the landing page bookstore section.
 *
 * Props:
 *   title        — carousel heading
 *   fetchUrl     — API URL returning { books: [...] } or { books: [...], ... }
 *   browseHref   — "Browse all →" link destination
 *   visibleCount — how many book cards to show at once (default 4)
 *   emptyLabel   — text to show when no books are available
 *   gatedHref    — if set, individual book card clicks redirect here
 *                  (use for adults-only / gated genres so the age gate fires)
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { ChevronLeft, ChevronRight, BookOpen, X, ArrowRight, AlertTriangle } from 'lucide-react';
import BookDetailModal from '@/components/bookstore/BookDetailModal';
import GenreContentWarningDialog, { hasAcceptedContentWarning } from '@/components/bookstore/GenreContentWarningDialog';

const INTERVAL = 4500; // ms between auto-advances (only used when autoPlay=true)

const GENRE_LABELS = {
  'dark-romance':       'Dark Romance',
  'grey-romance':       'Grey Romance',
  'romance':            'Romance',
  'new-romance':        'Romance',
  'historical-romance': 'Historical',
  'steampunk-romance':  'Steampunk',
  'gothic-mystery':     'Gothic',
  'fantasy':            'Fantasy',
  'epic-fantasy':       'Fantasy',
  'dark-fantasy':       'Dark Fantasy',
  'science-fiction':    'Sci-Fi',
  'sci-fi':             'Sci-Fi',
  'nonfiction':         'Non-Fiction',
  'fiction':            'Fiction',
  'lgbtqia':            'LGBTQIA+',
  'mystery':            'Mystery',
  'adult-fiction':      'Adult Fiction',
  'erotica':            'Erotica',
};

function formatPrice(cents) {
  if (!cents || cents === 0) return 'Free';
  return `$${(cents / 100).toFixed(2)}`;
}

function BookCard({ book, gatedHref, onOpenModal, dark = false, onGatedClick = null, fillHeight = false }) {
  const cover = book.cover_front_url || book.cover_url || null;
  const genreLabel = GENRE_LABELS[book.genre_slug] || null;
  const genreHref  = book.genre_slug
    ? `/bookstore/g/${book.genre_slug}`
    : null;

  const cardContent = (
    <>
      {/* Cover */}
      <div className={`relative overflow-hidden rounded border border-[#c6a664]/30 shadow-md bg-[#1e1410] ${
        fillHeight ? 'flex-1 min-h-0' : 'aspect-[2/3]'
      }`}>
        {cover ? (
          <img
            src={cover}
            alt={book.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-2 p-4">
            <BookOpen className="h-10 w-10 text-[#c6a664]/40" />
            <p className="text-[11px] text-[#c6a664]/50 text-center font-serif leading-tight">
              {book.title}
            </p>
          </div>
        )}
        {/* Hover overlay */}
        <div className="absolute inset-0 bg-[#E57A1C]/0 group-hover:bg-[#E57A1C]/20
                        transition-all duration-300 flex items-center justify-center">
          <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300
                           text-[11px] font-semibold text-white bg-[#E57A1C] px-3 py-1.5
                           rounded border border-[#E57A1C]/40">
            {gatedHref ? 'View & Buy' : 'Look Inside'}
          </span>
        </div>
      </div>

      {/* Info row */}
      <div className="px-0.5 space-y-0.5">
        <p className={`text-[13px] font-semibold leading-tight line-clamp-2 transition-colors font-serif
                      ${dark ? 'text-[#f0dde0] group-hover:text-white' : 'text-[#2a1f18] group-hover:text-[#5a1c1f]'}`}>
          {book.title}
        </p>
        {book.author && (
          <p className={`text-[10.5px] italic truncate tracking-wide
                        ${dark ? 'text-[#e8c7cc]/70' : 'text-[#7a5b1a]/80'}`}>
            by {book.author}
          </p>
        )}
        {/* PG blurb snippet — shown only on gated (Dark Romance) carousel cards */}
        {gatedHref && book.back_copy && (
          <p className={`text-[10px] leading-snug line-clamp-2 pt-0.5
                        ${dark ? 'text-[#e8c7cc]/55' : 'text-[#5a1c1f]/70'}`}>
            {book.back_copy.replace(/\n/g, ' ').slice(0, 120)}&hellip;
          </p>
        )}
        <div className="flex items-center gap-1.5 pt-0.5 flex-wrap">
          <span
            className="text-[11px] font-bold tracking-wide"
            style={{ color: '#E57A1C' }}
            data-testid={`book-price-${book.id}`}
          >
            {formatPrice(book.price_cents)}
          </span>
          {genreLabel && genreHref && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); window.location.href = genreHref; }}
              className="text-[9.5px] px-1.5 py-0.5 rounded-full border transition-colors
                         hover:bg-[#c6a664]/30 cursor-pointer"
              style={{
                backgroundColor: '#f5ede0',
                borderColor:     '#c6a664',
                color:           '#7a5b1a',
              }}
              data-testid={`book-genre-chip-${book.genre_slug}`}
            >
              {genreLabel}
            </button>
          )}
        </div>
      </div>
    </>
  );

  /* Gated genres (age-gate, adults-only) → PG preview then adult gate */
  if (gatedHref) {
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={() => onGatedClick ? onGatedClick(book) : (window.location.href = gatedHref)}
        onKeyDown={(e) => e.key === 'Enter' && (onGatedClick ? onGatedClick(book) : (window.location.href = gatedHref))}
        className={`group flex flex-col gap-1.5 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 rounded${fillHeight ? ' h-full' : ''}`}
        aria-label={`View ${book.title} by ${book.author}`}
        data-testid={`landing-carousel-book-${book.id}`}
      >
        {cardContent}
      </div>
    );
  }

  /* All other carousels → open BookDetailModal (Look Inside) */
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpenModal(book)}
      onKeyDown={(e) => e.key === 'Enter' && onOpenModal(book)}
      className={`group flex flex-col gap-1.5 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 rounded${fillHeight ? ' h-full' : ''}`}
      aria-label={`Look inside ${book.title} by ${book.author}`}
      data-testid={`landing-carousel-book-${book.id}`}
    >
      {cardContent}
    </div>
  );
}

export default function LandingBookCarousel({
  title,
  eyebrow = null,
  fetchUrl,
  browseHref = '/bookstore',
  visibleCount = 4,
  emptyLabel = 'No titles available yet.',
  onEmptyClick = null,
  emptyImage = null,
  gatedHref = null,
  accentColor = null,   // override border/eyebrow colour (e.g. Dark Romance)
  autoPlay = false,     // disabled by default — user must navigate manually
  pinnedTitle = null,   // book title to always show first
  fixedHeight = null,   // when set, carousel height matches the gateway panel exactly
}) {
  const [books,        setBooks]        = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [index,        setIndex]        = useState(0);
  const [paused,       setPaused]       = useState(false);
  const [selectedBook, setSelectedBook] = useState(null);   // Look Inside modal
  const [previewBook,  setPreviewBook]  = useState(null);   // PG blurb preview (gated genres)
  const [adultGateOpen, setAdultGateOpen] = useState(false); // Adult content gate step 2
  const timerRef = useRef(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetch(fetchUrl)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!active) return;
        let list = d?.books || [];
        // Pin a specific book to the front of the carousel
        if (pinnedTitle && list.length > 1) {
          const pinnedLower = pinnedTitle.toLowerCase();
          const pinIdx = list.findIndex(
            (b) => b.title?.toLowerCase() === pinnedLower
          );
          if (pinIdx > 0) {
            const [pinned] = list.splice(pinIdx, 1);
            list = [pinned, ...list];
          }
        }
        setBooks(list);
      })
      .catch(() => {})
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [fetchUrl, pinnedTitle]);

  const maxIndex = Math.max(0, books.length - visibleCount);

  // Derive the genre slug from gatedHref for the adult gate (e.g. /bookstore/g/dark-romance → dark-romance)
  const gatedSlug = gatedHref ? (gatedHref.split('/').pop() || 'dark-romance') : 'dark-romance';
  const gatedGenreName = GENRE_LABELS[gatedSlug] || title || 'Dark Romance';

  // 2-step gated click: if already accepted → go direct; else show PG blurb first
  const handleGatedClick = (book) => {
    if (hasAcceptedContentWarning(gatedSlug)) {
      window.location.href = gatedHref;
      return;
    }
    setPreviewBook(book);
  };

  useEffect(() => {
    if (!autoPlay || paused || books.length <= visibleCount) return;
    timerRef.current = setInterval(() => {
      setIndex((i) => (i >= maxIndex ? 0 : i + 1));
    }, INTERVAL);
    return () => clearInterval(timerRef.current);
  }, [autoPlay, paused, books.length, maxIndex, visibleCount]);

  const prev = () => { clearInterval(timerRef.current); setIndex((i) => Math.max(0, i - 1)); };
  const next = () => { clearInterval(timerRef.current); setIndex((i) => Math.min(maxIndex, i + 1)); };

  if (loading) {
    return (
      <div className="w-full rounded-xl border border-[#c6a664]/30 bg-[#fdf6e4] p-6
                      flex flex-col gap-4 min-h-[320px] justify-center"
           data-testid={`landing-carousel-loading-${title?.replace(/\s+/g,'-').toLowerCase()}`}>
        <div className="h-5 w-48 bg-[#c6a664]/20 rounded animate-pulse" />
        <div className={`grid gap-3`} style={{ gridTemplateColumns: `repeat(${visibleCount}, minmax(0, 1fr))` }}>
          {Array.from({ length: visibleCount }).map((_, n) => (
            <div key={n} className="aspect-[2/3] bg-[#c6a664]/10 rounded animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (books.length === 0) {
    /* ── Image placeholder (e.g. Bookworm / Thinker for Non-Fiction) ── */
    if (emptyImage) {
      return (
        <div
          className={`w-full rounded-xl border border-[#c6a664]/40 overflow-hidden relative min-h-[320px]
                      ${onEmptyClick ? 'cursor-pointer' : ''}`}
          onClick={onEmptyClick || undefined}
          role={onEmptyClick ? 'button' : undefined}
          tabIndex={onEmptyClick ? 0 : undefined}
          onKeyDown={onEmptyClick ? (e) => e.key === 'Enter' && onEmptyClick() : undefined}
          data-testid={`landing-carousel-empty-${title?.replace(/\s+/g,'-').toLowerCase()}`}
        >
          <img
            src={emptyImage}
            alt={title}
            className="absolute inset-0 w-full h-full object-cover"
          />
          {/* gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#1a0e05]/85 via-[#1a0e05]/20 to-transparent" />
          {/* text */}
          <div className="absolute inset-0 flex flex-col items-center justify-end p-6 gap-1.5 text-center">
            <p className="text-[13px] font-semibold uppercase tracking-[0.22em] text-[#f5e6c8]">
              {title}
            </p>
            <p className="text-[11px] text-[#d4a853]/80">{emptyLabel}</p>
            {onEmptyClick && (
              <span className="text-[10.5px] text-[#d4a853] font-medium mt-0.5 hover:text-[#f0c96a] transition-colors">
                Click to learn more →
              </span>
            )}
          </div>
        </div>
      );
    }

    /* ── Default empty state (no image) ── */
    return (
      <div
        className={`w-full rounded-xl border border-[#c6a664]/30 bg-[#fdf6e4] p-6
                    flex flex-col items-center justify-center min-h-[220px] gap-3
                    ${onEmptyClick ? 'cursor-pointer hover:border-[#c6a664]/60 hover:shadow-md transition-all duration-200' : ''}`}
        onClick={onEmptyClick || undefined}
        role={onEmptyClick ? 'button' : undefined}
        tabIndex={onEmptyClick ? 0 : undefined}
        onKeyDown={onEmptyClick ? (e) => e.key === 'Enter' && onEmptyClick() : undefined}
        data-testid={`landing-carousel-empty-${title?.replace(/\s+/g,'-').toLowerCase()}`}
      >
        <BookOpen className="h-10 w-10 text-[#c6a664]/30" />
        <p className="text-[12px] text-[#7a5b1a]/60">{emptyLabel}</p>
        {onEmptyClick && (
          <span className="text-[11px] text-[#5a1c1f]/60 font-medium mt-1 hover:text-[#5a1c1f]">
            Click to learn more →
          </span>
        )}
      </div>
    );
  }

  const visible = books.slice(index, index + visibleCount);
  // Peek: grab the first card of the next page when there's more to show
  const peekBook = (index < maxIndex && books[index + visibleCount]) || null;

  return (
    <>
    <style>{`
      @keyframes peek-breathe {
        0%   { opacity: 0.28; transform: translateX(6px); }
        55%  { opacity: 0.42; transform: translateX(0px); }
        100% { opacity: 0.28; transform: translateX(6px); }
      }
      .carousel-peek-card {
        animation: peek-breathe 2.6s ease-in-out infinite;
        pointer-events: none;
        user-select: none;
      }
    `}</style>
    <div
      className="w-full rounded-xl overflow-hidden flex flex-col min-h-[360px]"
      style={{
        border: `1px solid ${accentColor ? accentColor + '55' : '#c6a664'}`,
        background: accentColor ? '#1a0a0c' : '#fdf6e4',
        boxShadow: accentColor
          ? `0 2px 22px ${accentColor}33`
          : '0 2px 22px rgba(90,28,31,0.10)',
        ...(fixedHeight ? { height: `${fixedHeight}px`, minHeight: 'unset' } : {}),
      }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid={`landing-carousel-${title?.replace(/\s+/g,'-').toLowerCase()}`}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-5 pt-5 pb-3"
        style={{ borderBottom: `1px solid ${accentColor ? accentColor + '44' : '#c6a664'}33` }}
      >
        <div className="flex items-center gap-2">
          <BookOpen className="h-4 w-4" style={{ color: accentColor ?? '#7a5b1a' }} />
          <span
            className="text-[12.5px] font-semibold uppercase tracking-widest"
            style={{ color: accentColor ? '#e8c7cc' : '#7a5b1a' }}
          >
            {title}
          </span>
          {eyebrow && (
            <span
              className="text-[9.5px] font-bold uppercase tracking-[0.15em] px-1.5 py-0.5 rounded-sm border"
              style={{
                background:   accentColor ? accentColor : '#E57A1C',
                color:        '#fff',
                borderColor:  accentColor ? accentColor + '88' : '#E57A1C88',
                opacity: 0.92,
              }}
            >
              {eyebrow}
            </span>
          )}
        </div>
        <a
          href={browseHref}
          className="text-[11px] font-medium hover:underline underline-offset-2"
          style={{ color: accentColor ? '#e8c7cc' : '#5a1c1f' }}
        >
          Browse all →
        </a>
      </div>

      {/* Carousel track */}
      <div className="px-5 pt-5 pb-3 flex-1 overflow-hidden flex flex-col">
        <div
          className={`grid gap-4${fixedHeight ? ' h-full' : ''}`}
          style={{
            gridTemplateColumns: peekBook
              ? `repeat(${Math.min(visibleCount, visible.length)}, minmax(0, 1fr)) 22%`
              : `repeat(${Math.min(visibleCount, visible.length)}, minmax(0, 1fr))`,
          }}
        >
          {visible.map((book) => (
            <BookCard key={book.id} book={book} gatedHref={gatedHref} onOpenModal={setSelectedBook} dark={!!accentColor} onGatedClick={gatedHref ? handleGatedClick : null} fillHeight={!!fixedHeight} />
          ))}
          {/* Peek card — partial next cover hints at more content */}
          {peekBook && (
            <div className="carousel-peek-card overflow-hidden relative rounded"
                 aria-hidden="true">
              <div style={{ width: '455%', marginLeft: 0 }}>
                <BookCard book={peekBook} gatedHref={gatedHref} onOpenModal={() => {}} dark={!!accentColor} />
              </div>
              {/* Fade-out mask toward the right edge */}
              <div className="absolute inset-0 pointer-events-none"
                   style={{
                     background: `linear-gradient(to right, transparent 30%, ${accentColor ? '#1a0a0c' : '#fdf6e4'} 100%)`,
                   }} />
            </div>
          )}
        </div>
      </div>

      {/* Navigation footer */}
      {books.length > visibleCount && (
        <div className="flex items-center justify-between px-5 pb-5 pt-1">
          <div className="flex items-center gap-1.5">
            {Array.from({ length: maxIndex + 1 }).map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => { clearInterval(timerRef.current); setIndex(i); }}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === index
                    ? 'w-5 bg-[#E57A1C]'
                    : 'w-1.5 bg-[#E57A1C]/30 hover:bg-[#E57A1C]/60'
                }`}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={prev}
              disabled={index === 0}
              className="h-8 w-8 rounded flex items-center justify-center
                         text-[#7a5b1a] hover:bg-[#c6a664]/20 disabled:opacity-30
                         disabled:cursor-not-allowed transition-colors"
              aria-label="Previous books"
              data-testid="carousel-large-prev"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={next}
              disabled={index >= maxIndex}
              className="h-8 w-8 rounded flex items-center justify-center
                         text-[#7a5b1a] hover:bg-[#c6a664]/20 disabled:opacity-30
                         disabled:cursor-not-allowed transition-colors"
              aria-label="Next books"
              data-testid="carousel-large-next"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}
    </div>

    {/* Look Inside modal — opens when a non-gated book card is clicked */}
    {selectedBook && (
      <BookDetailModal
        book={selectedBook}
        onClose={() => setSelectedBook(null)}
      />
    )}

    {/* ── Step 1: PG blurb preview — click on a gated card shows this first ── */}
    {previewBook && (
      <div
        className="fixed inset-0 z-50 flex items-end justify-center sm:items-center p-4"
        style={{ backgroundColor: 'rgba(10,4,2,0.78)', backdropFilter: 'blur(4px)' }}
        onClick={() => setPreviewBook(null)}
        data-testid="dr-pg-preview-backdrop"
      >
        <div
          className="relative w-full max-w-md rounded-xl overflow-hidden shadow-2xl"
          style={{ background: '#1a0a0c', border: '1px solid #c0394a55' }}
          onClick={(e) => e.stopPropagation()}
          data-testid="dr-pg-preview-modal"
        >
          {/* Header */}
          <div className="flex items-start gap-4 p-5">
            {previewBook.cover_front_url || previewBook.cover_url ? (
              <img
                src={previewBook.cover_front_url || previewBook.cover_url}
                alt={previewBook.title}
                className="w-20 shrink-0 rounded aspect-[2/3] object-cover border border-[#c0394a]/30"
              />
            ) : null}
            <div className="flex-1 min-w-0">
              <p className="text-[9px] font-bold uppercase tracking-[0.2em] mb-1" style={{ color: '#c0394a' }}>
                Dark Romance · 18 +
              </p>
              <h3 className="text-[17px] font-bold leading-tight text-white font-serif">{previewBook.title}</h3>
              {previewBook.author && (
                <p className="text-[12px] italic mt-0.5" style={{ color: '#e8c7cc99' }}>by {previewBook.author}</p>
              )}
            </div>
            <button
              type="button"
              onClick={() => setPreviewBook(null)}
              className="shrink-0 p-1 rounded hover:bg-white/10 text-[#e8c7cc]/60"
              aria-label="Close preview"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* PG blurb */}
          {previewBook.back_copy && (
            <div className="px-5 pb-4">
              <p className="text-[12.5px] leading-relaxed" style={{ color: '#e8c7cc' }}>
                {previewBook.back_copy.slice(0, 320)}{previewBook.back_copy.length > 320 ? '…' : ''}
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2.5 px-5 pb-5">
            <button
              type="button"
              onClick={() => { setPreviewBook(null); setAdultGateOpen(true); }}
              data-testid="dr-pg-preview-continue-btn"
              className="flex-1 rounded-lg py-2.5 text-[13px] font-semibold text-white transition hover:brightness-110"
              style={{ backgroundColor: '#c0394a' }}
            >
              Continue to shelf →
            </button>
            <button
              type="button"
              onClick={() => setPreviewBook(null)}
              data-testid="dr-pg-preview-dismiss-btn"
              className="rounded-lg border px-4 py-2.5 text-[13px] transition hover:bg-white/10"
              style={{ borderColor: '#c6a664/40', color: '#e8c7cc/70' }}
            >
              Not for me
            </button>
          </div>
        </div>
      </div>
    )}

    {/* ── Step 2: Adult content gate ── */}
    <GenreContentWarningDialog
      genre={adultGateOpen ? { slug: gatedSlug, name: gatedGenreName } : null}
      onAcceptOnce={() => { setAdultGateOpen(false); window.location.href = gatedHref; }}
      onRetreat={() => setAdultGateOpen(false)}
    />
    </>
  );
}
